import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useRecoilState, useResetRecoilState, useSetRecoilState } from 'recoil';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  lighten,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Close as CloseIcon } from '@mui/icons-material';

import { userCloneFilterState } from 'atoms/WarehouseFilterState';
import { turnOnCloneUserState } from 'atoms/WarehouseState';
import { warehouseUserState } from 'atoms/WarehouseUserState';
import ListHeader from 'components/ListHeader';
import { addWarehouseUsers, listWarehouse, listWarehouseUsers } from 'repositories/HttpRepository';
import { processType } from '../../helpers';

const headCells = [
  { id: '', label: '', align: 'center' },
  { id: 'alias', label: 'Warehouse alias', align: 'left' },
  { id: 'id', label: 'ID' },
  { id: 'owner_type', label: 'Type', isDisableSort: true },
  { id: 'street', label: 'Address', align: 'center', isDisableSort: true },
];

function EnhancedTableHead() {
  const [filterStates, setFilterState] = useRecoilState(userCloneFilterState);
  const { is_desc, order_by } = filterStates.sort;

  const handleRequestSort = (event, property) => {
    const isDesc = order_by === property && is_desc;
    setFilterState({
      ...filterStates,
      sort: {
        is_desc: !isDesc,
        order_by: property,
      },
    });
  };

  return (
    <ListHeader fields={headCells} desc={is_desc} orderBy={order_by} onRequestOrder={handleRequestSort} rowCount="1" />
  );
}

const useStyles = makeStyles()((theme) => ({
  Paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
    overflowY: 'auto',
    boxShadow: 'none',
    minHeight: 200,
    maxHeight: 350,
  },
  table: {
    minWidth: 750,
  },
  Table: {
    marginTop: 20,

    '& thead': {
      backgroundColor: theme.palette.secondary.main,

      '& th': {
        color: '#fff',
      },
    },
    '& tbody': {
      backgroundColor: lighten(theme.palette.primary.main, 0.9),
    },
  },
  CloseBtn: {
    padding: theme.spacing(1),
  },
  EmptyRow: {
    height: 45,
  },
  closeBtn: {
    position: 'absolute',
    right: 20,
    top: 20,
    color: '#d8d8d8',
    fontSize: 30,
    cursor: 'pointer',
  },
}));

function WarehouseSearchInput(props) {
  const { onSubmit, onReset } = props;
  const { classes } = useStyles();
  const [keyword, setKeyword] = useState('');

  const handleChange = (e) => {
    setKeyword(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter') {
      return;
    }
    e.preventDefault();
    onSubmit(e.target.value);
  };

  const handleReset = () => {
    setKeyword('');
    onReset();
  };

  return (
    <TextField
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      variant="outlined"
      placeholder="Search warehouse by warehouse alias, warehouse ID…"
      fullWidth
      value={keyword}
      size="small"
      slotProps={{
        input: {
          endAdornment: keyword && (
            <IconButton className={classes.CloseBtn} onClick={handleReset} size="large">
              <CloseIcon />
            </IconButton>
          ),
        },
      }}
    />
  );
}

function CloneUsers(props) {
  const { warehouse_id } = props;
  const { classes } = useStyles();
  const [selected, setSelected] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [saving, setSaving] = useState(false);

  const setTurnOnCloneUser = useSetRecoilState(turnOnCloneUserState);
  const [existedUsers, setExistedUsers] = useRecoilState(warehouseUserState);
  const [filterStates, setFilterState] = useRecoilState(userCloneFilterState);
  const resetFilterState = useResetRecoilState(userCloneFilterState);

  const existedUserIds = existedUsers.map((existedUser) => existedUser.id);

  const handleClick = (id) => {
    setSelected([id]);
  };

  const handleSearchSubmit = (q) => {
    setFilterState({
      ...filterStates,
      selected: {
        ...filterStates.selected,
        q,
      },
    });
  };

  const handleSearchReset = () => {
    setSelected([]);
    resetFilterState();
  };

  const processFilterData = () => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filterStates.selected)) {
      if (!value) {
        continue;
      }

      if (Array.isArray(value)) {
        value.forEach((tmp) => {
          params.append(key, tmp);
        });
      } else {
        params.append(key, value);
      }
    }
    params.append('order_by', filterStates.sort.order_by);
    params.append('desc', filterStates.sort.is_desc);
    params.append('size', 999);

    return params;
  };

  useEffect(() => {
    listWarehouse(processFilterData()).then((resp) => {
      if (resp.ok) {
        setWarehouses(resp.data.items || []);
      }
    });
  }, [filterStates]);

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const handleClose = () => {
    setTurnOnCloneUser(false);
    resetFilterState();
  };

  const handleSave = () => {
    setSaving(true);
    listWarehouseUsers(selected, { size: 200 })
      .then((resp) => {
        if (!resp.ok) {
          return undefined;
        }

        const users = resp.data?.items ?? [];
        const addUsers = users.filter((user) => !existedUserIds.includes(user.id));
        const addUserIds = addUsers.map((user) => user.id);

        if (addUserIds.length === 0) {
          toast.info('All users of the selected warehouse already belong to this warehouse.');
          return undefined;
        }

        return addWarehouseUsers(warehouse_id, addUserIds).then((res) => {
          if (res.ok) {
            toast.success('Added ' + addUserIds.length + ' users !');
            setExistedUsers([...existedUsers, ...addUsers]);
            handleClose();
          } else {
            toast.error('Server is having problem, please try again later.');
          }
        });
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <Dialog open onClose={handleClose} aria-labelledby="form-dialog-title" maxWidth="md">
      <DialogTitle id="form-dialog-title" variant="h6" sx={{ fontWeight: 700 }}>
        Clone Users
        <CloseIcon className={classes.closeBtn} onClick={handleClose} />
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body1" sx={{ mb: 1 }}>
          Select a warehouse to clone its users
        </Typography>
        <WarehouseSearchInput onSubmit={handleSearchSubmit} onReset={handleSearchReset} />
        <div className={classes.Table}>
          <Paper className={classes.Paper}>
            <TableContainer>
              <Table className={classes.table} aria-labelledby="tableTitle" aria-label="enhanced table">
                <EnhancedTableHead />
                <TableBody>
                  {warehouses.length !== 0
                    ? warehouses.map((row) => {
                        const isItemSelected = isSelected(row.id);

                        return (
                          <TableRow
                            hover
                            onClick={() => handleClick(row.id)}
                            role="checkbox"
                            aria-checked={isItemSelected}
                            tabIndex={-1}
                            key={row.id}
                            selected={isItemSelected}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isItemSelected}
                                size="small"
                                color="primary"
                                slotProps={{
                                  input: { 'aria-label': row.alias },
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">{row.alias}</TableCell>
                            <TableCell align="left">{row.id}</TableCell>
                            <TableCell align="left">{processType(row.owner_type, row.client_name)}</TableCell>
                            <TableCell align="center">{row.address.street}</TableCell>
                          </TableRow>
                        );
                      })
                    : [1, 2, 3, 4, 5].map((row) => (
                        <TableRow className={classes.EmptyRow} key={`empty-row-${row}`}>
                          <TableCell align="center" colSpan={5} />
                        </TableRow>
                      ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </div>
      </DialogContent>
      <DialogActions>
        <Button size="medium" variant="contained" onClick={handleClose} color="inherit">
          CANCEL
        </Button>
        <Button size="medium" variant="contained" disabled={selected.length === 0 || saving} onClick={handleSave}>
          CLONE
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CloneUsers;
